import { useState, useMemo } from 'react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, addWeeks, subWeeks,
  isSameMonth, isSameDay, isToday, parseISO,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Clock, Phone, Mail, Car, X, Edit3, User, FileText, MapPin, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

function parseAppointmentDate(apt) {
  if (!apt.appointment_date) return null;
  try {
    const d = typeof apt.appointment_date === 'string' ? parseISO(apt.appointment_date) : new Date(apt.appointment_date);
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
}

// ── Mini Calendar (Left Sidebar) ──
function MiniCalendar({ currentDate, onDateSelect, onMonthChange, appointments, theme: th }) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const weeks = [];
  let day = calStart;
  while (day <= calEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(day));
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  const hasApts = (d) => appointments.some(a => { const ad = parseAppointmentDate(a); return ad && isSameDay(ad, d); });

  return (
    <div className="select-none">
      {/* Mini header */}
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-medium ${th.dark ? 'text-gray-200' : 'text-gray-800'}`}>
          {format(currentDate, 'MMMM yyyy')}
        </span>
        <div className="flex items-center gap-0.5">
          <button onClick={() => onMonthChange(-1)} className={`p-0.5 rounded-full ${th.dark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}>
            <ChevronLeft className={`w-4 h-4 ${th.dark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
          <button onClick={() => onMonthChange(1)} className={`p-0.5 rounded-full ${th.dark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}>
            <ChevronRight className={`w-4 h-4 ${th.dark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className={`text-center text-[10px] font-medium ${th.dark ? 'text-gray-500' : 'text-gray-400'}`}>{d}</div>
        ))}
      </div>
      {/* Date grid */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7">
          {week.map((d, di) => {
            const inMonth = isSameMonth(d, currentDate);
            const today = isToday(d);
            const selected = isSameDay(d, currentDate) && !today;
            const hasAppointment = hasApts(d);
            return (
              <button
                key={di}
                onClick={() => onDateSelect(d)}
                className={`w-7 h-7 flex items-center justify-center text-[11px] rounded-full transition-colors relative
                  ${today ? 'bg-[#1a73e8] text-white font-bold' : ''}
                  ${selected && !today ? (th.dark ? 'bg-[#394457] text-white' : 'bg-blue-100 text-blue-800') : ''}
                  ${!today && !selected && inMonth ? (th.dark ? 'text-gray-300 hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100') : ''}
                  ${!inMonth ? (th.dark ? 'text-gray-600' : 'text-gray-300') : ''}
                `}
              >
                {format(d, 'd')}
                {hasAppointment && !today && (
                  <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-[#1a73e8]" />
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Legend Sidebar ──
function LegendSidebar({ showTestDrive, showDealerVisit, showCancelled, onToggleTestDrive, onToggleDealerVisit, onToggleCancelled, theme: th }) {
  return (
    <div className="mt-6">
      <h3 className={`text-xs font-semibold mb-2 ${th.dark ? 'text-gray-400' : 'text-gray-500'}`}>Appointment Types</h3>
      <button
        onClick={onToggleTestDrive}
        className={`flex items-center gap-2 w-full py-1.5 px-1 rounded transition-opacity ${showTestDrive ? '' : 'opacity-30'} ${th.dark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
      >
        <div className="w-3 h-3 rounded-sm bg-[#039be5]" />
        <span className={`text-xs ${th.dark ? 'text-gray-300' : 'text-gray-700'}`}>Test Drive</span>
      </button>
      <button
        onClick={onToggleDealerVisit}
        className={`flex items-center gap-2 w-full py-1.5 px-1 rounded transition-opacity ${showDealerVisit ? '' : 'opacity-30'} ${th.dark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
      >
        <div className="w-3 h-3 rounded-sm bg-[#7986cb]" />
        <span className={`text-xs ${th.dark ? 'text-gray-300' : 'text-gray-700'}`}>Dealer Visit</span>
      </button>
      <button
        onClick={onToggleCancelled}
        className={`flex items-center gap-2 w-full py-1.5 px-1 rounded transition-opacity ${showCancelled ? '' : 'opacity-30'} ${th.dark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
      >
        <div className="w-3 h-3 rounded-sm bg-[#ea4335]" />
        <span className={`text-xs ${th.dark ? 'text-gray-300' : 'text-gray-700'}`}>Cancelled</span>
      </button>
    </div>
  );
}

// ── Month View ──
function MonthView({ currentDate, appointments, onDateClick, onAppointmentClick, theme: th }) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const weeks = [];
  let day = calStart;
  while (day <= calEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(day));
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  const borderColor = th.dark ? 'border-[#303134]' : 'border-gray-200';

  return (
    <div className="flex flex-col flex-1 h-full">
      {/* Day headers */}
      <div className={`grid grid-cols-7 border-b ${borderColor}`}>
        {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d => (
          <div key={d} className={`text-center text-[11px] font-medium py-2 border-r ${borderColor} ${th.dark ? 'text-gray-500' : 'text-gray-500'}`}>{d}</div>
        ))}
      </div>
      {/* Weeks — fill all remaining height */}
      <div className="flex-1 grid" style={{ gridTemplateRows: `repeat(${weeks.length}, 1fr)` }}>
        {weeks.map((week, wi) => (
          <div key={wi} className={`grid grid-cols-7 border-b ${borderColor} min-h-0`}>
            {week.map((d, di) => {
              const dayApts = appointments.filter(a => {
                const ad = parseAppointmentDate(a);
                return ad && isSameDay(ad, d);
              });
              const inMonth = isSameMonth(d, currentDate);
              const today = isToday(d);
              return (
                <div
                  key={di}
                  onClick={() => onDateClick(d)}
                  className={`border-r ${borderColor} cursor-pointer overflow-hidden ${th.dark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                >
                  {/* Date number — centered at top */}
                  <div className="flex justify-center pt-1 pb-0.5">
                    <span className={`text-xs w-6 h-6 flex items-center justify-center rounded-full
                      ${today ? 'bg-[#1a73e8] text-white font-bold' : ''}
                      ${!today && inMonth ? (th.dark ? 'text-white' : 'text-black') : ''}
                      ${!inMonth ? (th.dark ? 'text-gray-600' : 'text-gray-400') : ''}
                    `}>
                      {format(d, 'd')}
                    </span>
                  </div>
                  {/* Events — full-width colored bars */}
                  <div className="space-y-0.5 px-0.5">
                    {dayApts.slice(0, 3).map(apt => {
                      const bgColor = apt.status === 'cancelled' ? 'bg-[#ea4335]' : apt.appointment_type === 'test_drive' ? 'bg-[#039be5]' : 'bg-[#7986cb]';
                      return (
                        <div
                          key={apt.conversation_id}
                          onClick={e => { e.stopPropagation(); onAppointmentClick(apt); }}
                          className={`text-[10px] leading-tight px-1.5 py-[3px] rounded-sm text-white truncate cursor-pointer hover:opacity-90 ${bgColor}`}
                        >
                          {apt.customer_name}
                        </div>
                      );
                    })}
                    {dayApts.length > 3 && (
                      <div className={`text-[10px] text-center ${th.dark ? 'text-gray-500' : 'text-gray-400'}`}>+{dayApts.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper: Get visible hours from business hours (1 hour before open, 1 hour after close)
function getVisibleHours(businessHours) {
  if (!businessHours || Object.keys(businessHours).length === 0) {
    return Array.from({ length: 16 }, (_, i) => i + 7); // default 7 AM to 10 PM
  }
  let earliest = 23, latest = 0;
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  for (const day of days) {
    const dh = businessHours[day];
    if (!dh || dh.closed) continue;
    const openStr = dh.open || '9:00 AM';
    const closeStr = dh.close || '5:00 PM';
    const openHour = parseTimeToHour(openStr);
    const closeHour = parseTimeToHour(closeStr);
    if (openHour < earliest) earliest = openHour;
    if (closeHour > latest) latest = closeHour;
  }
  if (earliest > latest) return Array.from({ length: 16 }, (_, i) => i + 7);
  const startHour = Math.max(0, earliest - 1);
  const endHour = Math.min(23, latest + 1);
  return Array.from({ length: endHour - startHour + 1 }, (_, i) => i + startHour);
}

function parseTimeToHour(timeStr) {
  if (!timeStr) return 9;
  const lower = timeStr.toLowerCase().trim();
  const match = lower.match(/(\d+)(?::(\d+))?\s*(am|pm)?/);
  if (!match) return 9;
  let hour = parseInt(match[1], 10);
  const isPM = match[3] === 'pm';
  const isAM = match[3] === 'am';
  if (isPM && hour !== 12) hour += 12;
  if (isAM && hour === 12) hour = 0;
  return hour;
}

// ── Week View ──
function WeekView({ currentDate, appointments, onAppointmentClick, onDateClick, theme: th, businessHours, timezone }) {
  const HOURS = getVisibleHours(businessHours);
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const borderColor = th.dark ? 'border-[#303134]' : 'border-gray-200';

  // Format timezone for display (e.g. "America/New_York" -> "EST" or short label)
  const tzLabel = timezone ? timezone.split('/').pop().replace(/_/g, ' ') : '';

  return (
    <div className="flex flex-col flex-1 overflow-auto">
      {/* Day headers with large date numbers like Google Calendar */}
      <div className={`grid grid-cols-[70px_repeat(7,1fr)] border-b ${borderColor} sticky top-0 z-10 ${th.dark ? 'bg-[#1a1a1b]' : 'bg-white'}`}>
        <div className={`text-[9px] text-right pr-1 pt-6 ${th.dark ? 'text-gray-600' : 'text-gray-400'}`}>
          {tzLabel}
        </div>
        {days.map(d => {
          const today = isToday(d);
          return (
            <div key={d.toISOString()} className={`text-center py-2 border-l ${borderColor} cursor-pointer ${th.dark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`} onClick={() => onDateClick(d)}>
              <div className={`text-[11px] uppercase tracking-wide ${today ? 'text-[#1a73e8]' : (th.dark ? 'text-gray-500' : 'text-gray-500')}`}>{format(d, 'EEE')}</div>
              <div className={`text-2xl font-normal inline-flex items-center justify-center w-11 h-11 rounded-full ${today ? 'bg-[#1a73e8] text-white' : (th.dark ? 'text-gray-300' : 'text-gray-700')}`}>{format(d, 'd')}</div>
            </div>
          );
        })}
      </div>
      <div className="flex-1">
        {HOURS.map(hour => (
          <div key={hour} className={`grid grid-cols-[70px_repeat(7,1fr)] border-b ${borderColor}`} style={{ height: '48px' }}>
            <div className={`text-[11px] text-right pr-2 pt-1 ${th.dark ? 'text-white' : 'text-black'}`}>
              {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
            </div>
            {days.map(d => {
              const hourApts = appointments.filter(a => {
                const ad = parseAppointmentDate(a);
                return ad && isSameDay(ad, d) && ad.getHours() === hour;
              });
              return (
                <div key={d.toISOString() + hour} className={`border-l ${borderColor} p-0.5`}>
                  {hourApts.map(apt => {
                    const bgColor = apt.status === 'cancelled' ? 'bg-[#ea4335]' : apt.appointment_type === 'test_drive' ? 'bg-[#039be5]' : 'bg-[#7986cb]';
                    return (
                      <div key={apt.conversation_id} onClick={() => onAppointmentClick(apt)}
                        className={`text-[10px] px-1 py-0.5 rounded text-white truncate cursor-pointer hover:opacity-90 ${bgColor}`}>
                        {apt.customer_name}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Day View ──
function DayView({ currentDate, appointments, onAppointmentClick, theme: th, businessHours, timezone }) {
  const HOURS = getVisibleHours(businessHours);
  const dayApts = appointments.filter(a => { const d = parseAppointmentDate(a); return d && isSameDay(d, currentDate); });
  const borderColor = th.dark ? 'border-[#303134]' : 'border-gray-200';

  return (
    <div className="flex flex-col flex-1 overflow-auto">
      <div className={`text-center py-2 border-b ${borderColor} sticky top-0 z-10 ${th.dark ? 'bg-[#1a1a1b]' : 'bg-white'}`}>
        <div className={`text-[11px] uppercase tracking-wide ${isToday(currentDate) ? 'text-[#1a73e8]' : (th.dark ? 'text-gray-500' : 'text-gray-500')}`}>{format(currentDate, 'EEEE')}</div>
        <div className={`text-2xl font-normal inline-flex items-center justify-center w-11 h-11 rounded-full ${isToday(currentDate) ? 'bg-[#1a73e8] text-white' : (th.dark ? 'text-gray-300' : 'text-gray-700')}`}>{format(currentDate, 'd')}</div>
      </div>
      <div className="flex-1">
        {HOURS.map(hour => {
          const hourApts = dayApts.filter(a => { const d = parseAppointmentDate(a); return d && d.getHours() === hour; });
          return (
            <div key={hour} className={`flex border-b ${borderColor}`} style={{ height: '48px' }}>
              <div className={`w-[70px] text-[11px] text-right pr-2 pt-1 shrink-0 ${th.dark ? 'text-white' : 'text-black'}`}>
                {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </div>
              <div className={`flex-1 border-l ${borderColor} p-0.5 space-y-0.5`}>
                {hourApts.map(apt => {
                  const bgColor = apt.status === 'cancelled' ? 'bg-[#ea4335]' : apt.appointment_type === 'test_drive' ? 'bg-[#039be5]' : 'bg-[#7986cb]';
                  return (
                    <div key={apt.conversation_id} onClick={() => onAppointmentClick(apt)}
                      className={`text-xs px-2 py-1.5 rounded text-white cursor-pointer hover:opacity-90 flex items-center gap-2 ${bgColor}`}>
                      <span className="font-medium">{apt.customer_name}</span>
                      <span className="opacity-80">{apt.vehicle}</span>
                      <Badge className="text-[9px] ml-auto bg-white/20 text-white border-0">
                        {apt.appointment_type === 'test_drive' ? 'Test Drive' : 'Dealer Visit'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Appointment Detail Modal ──
function AppointmentModal({ appointment, onClose, onSave, theme: th }) {
  const [editing, setEditing] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editType, setEditType] = useState('');
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);

  const apt = appointment;
  const aptDate = parseAppointmentDate(apt);
  const isTestDrive = apt.appointment_type === 'test_drive';
  const accentColor = isTestDrive ? '#039be5' : '#7986cb';

  const startEdit = () => {
    setEditing(true);
    if (aptDate) {
      setEditDate(format(aptDate, 'yyyy-MM-dd'));
      setEditTime(format(aptDate, 'HH:mm'));
    }
    setEditType(apt.appointment_type || 'test_drive');
    setEditReason(apt.visit_reason || '');
  };

  const handleSave = async () => {
    setSaving(true);
    const newDate = new Date(`${editDate}T${editTime}:00`);
    const display = format(newDate, "EEEE, MMM do 'at' h:mm a");
    await onSave(apt.conversation_id, {
      appointment_date: newDate.toISOString(),
      appointment_display: display,
      appointment_type: editType,
      visit_reason: editReason || undefined,
    });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className={`rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden ${th.dark ? 'bg-[#2d2e30]' : 'bg-white'}`}
        data-testid="appointment-modal"
      >
        {/* Color bar */}
        <div className="h-1.5" style={{ backgroundColor: accentColor }} />

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-3 pb-2">
          <Badge className="text-[10px] text-white border-0" style={{ backgroundColor: accentColor }}>
            {isTestDrive ? 'Test Drive' : 'Dealer Visit'}
          </Badge>
          <div className="flex items-center gap-1">
            {!editing && (
              <button onClick={startEdit} className={`p-1.5 rounded-full ${th.dark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <Edit3 className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className={`p-1.5 rounded-full ${th.dark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 pb-4 space-y-3">
          {!editing ? (
            <>
              <div className="flex items-start gap-3">
                <User className={`w-4 h-4 mt-0.5 shrink-0 ${th.dark ? 'text-gray-500' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${th.dark ? 'text-gray-200' : 'text-gray-800'}`}>{apt.customer_name}</span>
              </div>
              {apt.customer_phone && (
                <div className="flex items-center gap-3">
                  <Phone className={`w-4 h-4 shrink-0 ${th.dark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${th.dark ? 'text-gray-400' : 'text-gray-600'}`}>{apt.customer_phone}</span>
                </div>
              )}
              {apt.customer_email && (
                <div className="flex items-center gap-3">
                  <Mail className={`w-4 h-4 shrink-0 ${th.dark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${th.dark ? 'text-gray-400' : 'text-gray-600'}`}>{apt.customer_email}</span>
                </div>
              )}
              {apt.vehicle && (
                <div className="flex items-center gap-3">
                  <Car className={`w-4 h-4 shrink-0 ${th.dark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${th.dark ? 'text-gray-400' : 'text-gray-600'}`}>{apt.vehicle}</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Clock className={`w-4 h-4 shrink-0 ${th.dark ? 'text-gray-500' : 'text-gray-400'}`} />
                <span className={`text-sm ${th.dark ? 'text-gray-400' : 'text-gray-600'}`}>{apt.appointment_display || (aptDate ? format(aptDate, "EEEE, MMM do 'at' h:mm a") : 'No date')}</span>
              </div>
              {apt.visit_reason && (
                <div className="flex items-start gap-3">
                  <FileText className={`w-4 h-4 mt-0.5 shrink-0 ${th.dark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${th.dark ? 'text-gray-400' : 'text-gray-600'}`}>{apt.visit_reason}</span>
                </div>
              )}
              {apt.dealership_name && (
                <div className="flex items-center gap-3">
                  <MapPin className={`w-4 h-4 shrink-0 ${th.dark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${th.dark ? 'text-gray-400' : 'text-gray-600'}`}>{apt.dealership_name}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className={`text-xs block mb-1 ${th.dark ? 'text-gray-500' : 'text-gray-500'}`}>Date</label>
                <Input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className={`text-sm ${th.dark ? 'bg-[#3c4043] border-[#5f6368] text-white' : ''}`} />
              </div>
              <div>
                <label className={`text-xs block mb-1 ${th.dark ? 'text-gray-500' : 'text-gray-500'}`}>Time</label>
                <Input type="time" value={editTime} onChange={e => setEditTime(e.target.value)} className={`text-sm ${th.dark ? 'bg-[#3c4043] border-[#5f6368] text-white' : ''}`} />
              </div>
              <div>
                <label className={`text-xs block mb-1 ${th.dark ? 'text-gray-500' : 'text-gray-500'}`}>Type</label>
                <select value={editType} onChange={e => setEditType(e.target.value)}
                  className={`w-full rounded-md border px-3 py-2 text-sm ${th.dark ? 'bg-[#3c4043] border-[#5f6368] text-white' : 'bg-white border-gray-300 text-gray-700'}`}>
                  <option value="test_drive">Test Drive</option>
                  <option value="dealer_visit">Dealer Visit</option>
                </select>
              </div>
              {editType === 'dealer_visit' && (
                <div>
                  <label className={`text-xs block mb-1 ${th.dark ? 'text-gray-500' : 'text-gray-500'}`}>Reason</label>
                  <Input value={editReason} onChange={e => setEditReason(e.target.value)} placeholder="e.g. Financing options" className={`text-sm ${th.dark ? 'bg-[#3c4043] border-[#5f6368] text-white' : ''}`} />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving || !editDate || !editTime} className="text-white text-xs flex-1" style={{ backgroundColor: accentColor }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)} className={`text-xs ${th.dark ? 'border-[#5f6368] text-gray-300' : ''}`}>Cancel</Button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`px-5 py-2.5 border-t text-[10px] ${th.dark ? 'border-[#3c4043] text-gray-500' : 'border-gray-200 text-gray-400'}`}>
          Please contact the customer to confirm.
        </div>
      </div>
    </div>
  );
}

// ── Main Calendar Component ──
export default function PortalCalendar({ appointments, onUpdateAppointment, theme: th, businessHours = {}, timezone = '', dealerships = [], dealershipFilter = '', onDealershipFilterChange }) {
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedApt, setSelectedApt] = useState(null);
  const [showTestDrive, setShowTestDrive] = useState(true);
  const [showDealerVisit, setShowDealerVisit] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredAppointments = useMemo(() => {
    let filtered = appointments.filter(a => {
      if (a.status === 'cancelled') return showCancelled;
      if (a.appointment_type === 'test_drive') return showTestDrive;
      if (a.appointment_type === 'dealer_visit') return showDealerVisit;
      return true;
    });

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(a => {
        const name = (a.customer_name || '').toLowerCase();
        const phone = (a.customer_phone || '').toLowerCase();
        const email = (a.customer_email || '').toLowerCase();
        const vehicle = (a.vehicle || '').toLowerCase();
        const date = (a.appointment_display || '').toLowerCase();
        const dealership = (a.dealership_name || '').toLowerCase();
        const reason = (a.visit_reason || '').toLowerCase();
        const type = a.appointment_type === 'test_drive' ? 'test drive' : 'dealer visit';
        const status = (a.status || 'confirmed').toLowerCase();
        return name.includes(q) || phone.includes(q) || email.includes(q) ||
          vehicle.includes(q) || date.includes(q) || dealership.includes(q) ||
          reason.includes(q) || type.includes(q) || status.includes(q);
      });
    }

    return filtered;
  }, [appointments, showTestDrive, showDealerVisit, showCancelled, searchQuery]);

  const navigate = (dir) => {
    if (view === 'month') setCurrentDate(prev => dir === 1 ? addMonths(prev, 1) : subMonths(prev, 1));
    else if (view === 'week') setCurrentDate(prev => dir === 1 ? addWeeks(prev, 1) : subWeeks(prev, 1));
    else setCurrentDate(prev => addDays(prev, dir));
  };

  const title = useMemo(() => {
    if (view === 'month') return format(currentDate, 'MMMM yyyy');
    if (view === 'week') {
      const ws = startOfWeek(currentDate, { weekStartsOn: 0 });
      const we = endOfWeek(currentDate, { weekStartsOn: 0 });
      return `${format(ws, 'MMM d')} — ${format(we, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'EEEE, MMMM d, yyyy');
  }, [currentDate, view]);

  const handleDateClick = (day) => {
    setCurrentDate(day);
    setView('day');
  };

  const handleSave = async (conversationId, updates) => {
    await onUpdateAppointment(conversationId, updates);
    setSelectedApt(null);
  };

  const bgColor = th.dark ? 'bg-[#1a1a1b]' : 'bg-white';
  const borderColor = th.dark ? 'border-[#303134]' : 'border-gray-200';

  return (
    <div className={`flex ${bgColor} overflow-hidden`} style={{ height: 'calc(100vh - 120px)' }} data-testid="portal-calendar">
      {/* Left Sidebar */}
      <div className={`w-56 shrink-0 border-r ${borderColor} p-4 flex flex-col`}>
        <MiniCalendar
          currentDate={currentDate}
          onDateSelect={(d) => { setCurrentDate(d); setView('day'); }}
          onMonthChange={(dir) => setCurrentDate(prev => dir === 1 ? addMonths(prev, 1) : subMonths(prev, 1))}
          appointments={filteredAppointments}
          theme={th}
        />
        <LegendSidebar
          showTestDrive={showTestDrive}
          showDealerVisit={showDealerVisit}
          showCancelled={showCancelled}
          onToggleTestDrive={() => setShowTestDrive(prev => !prev)}
          onToggleDealerVisit={() => setShowDealerVisit(prev => !prev)}
          onToggleCancelled={() => setShowCancelled(prev => !prev)}
          theme={th}
        />

        {/* Search */}
        <div className="mt-6">
          <h3 className={`text-xs font-semibold mb-2 ${th.dark ? 'text-gray-400' : 'text-gray-500'}`}>Search Appointments</h3>
          <div className="relative">
            <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${th.dark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Name, phone, vehicle..."
              className={`w-full text-xs pl-7 pr-7 py-1.5 rounded border outline-none ${th.dark ? 'bg-[#2d2e30] border-[#5f6368] text-gray-300 placeholder:text-gray-600 focus:border-[#8ab4f8]' : 'bg-white border-gray-300 text-gray-700 placeholder:text-gray-400 focus:border-[#1a73e8]'}`}
              data-testid="calendar-search"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full ${th.dark ? 'hover:bg-white/10 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          {/* Autocomplete results */}
          {searchQuery.trim() && filteredAppointments.length > 0 && (
            <div className={`mt-1 rounded border max-h-48 overflow-y-auto ${th.dark ? 'bg-[#2d2e30] border-[#5f6368]' : 'bg-white border-gray-300 shadow-sm'}`}>
              {filteredAppointments.slice(0, 8).map(apt => {
                const bgColor = apt.status === 'cancelled' ? 'bg-[#ea4335]' : apt.appointment_type === 'test_drive' ? 'bg-[#039be5]' : 'bg-[#7986cb]';
                const aptDate = parseAppointmentDate(apt);
                return (
                  <button
                    key={apt.conversation_id}
                    onClick={() => {
                      setSelectedApt(apt);
                      if (aptDate) {
                        setCurrentDate(aptDate);
                        setView('day');
                      }
                    }}
                    className={`w-full text-left px-2 py-1.5 border-b last:border-b-0 flex items-center gap-2 ${th.dark ? 'border-[#3c4043] hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'}`}
                  >
                    <div className={`w-2 h-2 rounded-full shrink-0 ${bgColor}`} />
                    <div className="min-w-0 flex-1">
                      <div className={`text-[11px] font-medium truncate ${th.dark ? 'text-gray-200' : 'text-gray-800'}`}>{apt.customer_name}</div>
                      <div className={`text-[10px] truncate ${th.dark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {apt.vehicle || ''}{apt.appointment_display ? ` — ${apt.appointment_display}` : ''}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {searchQuery.trim() && filteredAppointments.length === 0 && (
            <div className={`mt-1 px-2 py-2 text-[10px] ${th.dark ? 'text-gray-500' : 'text-gray-400'}`}>No results found</div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className={`flex items-center justify-between px-4 py-2 border-b ${borderColor}`}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentDate(new Date())}
              className={`px-4 py-1.5 text-sm rounded border ${th.dark ? 'border-[#5f6368] text-gray-300 hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              Today
            </button>
            <button onClick={() => navigate(-1)} className={`p-1 rounded-full ${th.dark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
              <ChevronLeft className={`w-5 h-5 ${th.dark ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
            <button onClick={() => navigate(1)} className={`p-1 rounded-full ${th.dark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
              <ChevronRight className={`w-5 h-5 ${th.dark ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
            <h2 className={`text-xl font-normal ml-1 ${th.dark ? 'text-gray-200' : 'text-gray-700'}`}>{title}</h2>
          </div>
          <div className="flex items-center gap-3">
            {dealerships.length > 0 && (
              <select
                value={dealershipFilter}
                onChange={e => onDealershipFilterChange(e.target.value)}
                className={`text-xs rounded border h-8 px-2 ${th.dark ? 'bg-[#2d2e30] border-[#5f6368] text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                data-testid="calendar-dealer-filter"
              >
                <option value="">All Dealerships</option>
                {dealerships.map(d => (
                  <option key={d.dealership_id} value={d.dealership_id}>{d.name}</option>
                ))}
              </select>
            )}
            <div className={`flex rounded border overflow-hidden ${th.dark ? 'border-[#5f6368]' : 'border-gray-300'}`}>
            {['month', 'week', 'day'].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1 text-xs capitalize
                  ${view === v
                    ? (th.dark ? 'bg-[#394457] text-[#8ab4f8]' : 'bg-blue-50 text-[#1a73e8]')
                    : (th.dark ? 'text-gray-400 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-50')
                  }`}
              >
                {v}
              </button>
            ))}
          </div>
          </div>
        </div>

        {/* Calendar Body */}
        <div className="flex-1 overflow-auto">
          {view === 'month' && <MonthView currentDate={currentDate} appointments={filteredAppointments} onDateClick={handleDateClick} onAppointmentClick={setSelectedApt} theme={th} />}
          {view === 'week' && <WeekView currentDate={currentDate} appointments={filteredAppointments} onAppointmentClick={setSelectedApt} onDateClick={handleDateClick} theme={th} businessHours={businessHours} timezone={timezone} />}
          {view === 'day' && <DayView currentDate={currentDate} appointments={filteredAppointments} onAppointmentClick={setSelectedApt} theme={th} businessHours={businessHours} timezone={timezone} />}
        </div>
      </div>

      {/* Appointment Modal */}
      {selectedApt && <AppointmentModal appointment={selectedApt} onClose={() => setSelectedApt(null)} onSave={handleSave} theme={th} />}
    </div>
  );
}
